import { Component, ElementRef, EventEmitter, HostListener, Input, OnInit, Output, ViewChild, ViewContainerRef } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { RechercheReferentielsService } from 'src/app/services/rechercheReferentiels/recherche-referentiels.service';
import { ReferentielType, Result } from '../modal/recherche-referentiel/recherche-referentiel.component';
import * as moment from 'moment';

const minTermLength = 3;

@Component({
  selector: 'frg-form-input',
  templateUrl: './form-input.fragment.html',
  styleUrls: ['./form-input.fragment.scss']
})

export class FormInputFragment implements OnInit {


  @ViewChild('input') public inputElement: ElementRef;
  @ViewChild('listcontainer') public listContainer: ElementRef;

  @Input() public appAutoFocus = false;
  @Input() public formGroup: FormGroup;
  @Input() public control: FormControl;
  @Input() public required = false;
  @Input() public type: 'text' | 'password' | 'email' | 'hidden' | 'checkbox' | 'date' | 'ngSelect' | 'textarea' | 'colorSelect' | 'time' | 'number' | 'fake-number' | 'fake-int-number' | 'ref-search-input' | 'autocomplete' = 'text';
  @Input() public id = '';
  @Input() public name: string = this.id || '';
  @Input() public placeholder = '';
  @Input() public label = '';
  @Input() public minNumber: string;
  @Input() public maxNumber: string;
  @Input() public errorMessage = 'Les informations dans ce champ sont incorrectes';
  @Input() public selectValue?: any[];
  @Input() public checkSelectValueLength = false;
  @Input() public isMultiSelect: boolean = false;
  @Input() public closeOnSelect: boolean = true;
  @Input() public isClearableSelect: boolean = true;
  @Input() public radioTableDefinition?: any;
  @Input() public disabled = false;
  @Input() public deletable = false;
  @Input() public nbRows: string = '3'
  @Input() public minDate: string;
  @Input() public maxDate: string;
  @Input() public minTime: string;
  @Input() public maxTime: string;
  @Input() public isCheckLabelAlign: boolean = false;
  @Input() public isGreenLabel: boolean = false;
  @Input() public selectOnFocus: boolean = false;
  @Input() public isParentModal: boolean = false;
  @Input() public textareaResizable: boolean = true;
  @Input() public refSearchInput: Result;
  @Input() public autorizedRefs: Array<ReferentielType>;
  @Input() public searchFunction?: (search: string) => Promise<Array<{id: string, libelle: string}>>;
  @Output() public modelChanged = new EventEmitter();
  @Output() public blur: EventEmitter<void> = new EventEmitter();

  public autocompleteSearchInput$ = new Subject<string>();
  public autocompleteSearchMinTermLength = minTermLength;

  public error;
  public noeye = true;
  public selectOpen = false;

  public searchFunctionLoading = false;

  public dateMask = {
    guide: true,
    showMask: true,
    mask: [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
  };

  // HTML helpers
  objectFn = Object;

  @HostListener('document:click', ['$event'])
  clickout(event): void {
    if (!this._ref.nativeElement.contains(event.target) || this.disabled) {
      this.selectOpen = false;
    } else {
      this.selectOpen = !this.selectOpen;
    }
  }

  constructor(
    private _ref: ElementRef,
    private _rechercheRefSrv: RechercheReferentielsService,
    private viewCntnrRef: ViewContainerRef
  ) {}

  public async searchRef(): Promise<void> {
    const result = await this._rechercheRefSrv.initRecherche(this.viewCntnrRef, this.autorizedRefs);
    if (result && this.control) {
      this.control.patchValue(result);
      this.refSearchInput = result;
      this.changed(this.control);
    }
  }

  ngOnInit(): void {
    if (this.type === 'checkbox' && !this.control.value) {
      this.control.setValue(false);
    }

    if (this.type === 'autocomplete' && this.searchFunction) {
      this.autocompleteSearchInput$
        .pipe(debounceTime(500))
        .subscribe(async v => {
        if (v && v.length >= this.autocompleteSearchMinTermLength) {
          await this.callSearchFunc(v);
        }
      });
    }
  }

  ngAfterViewInit() {
    if (this.type === "colorSelect") {
      const colorValue = this.selectValue.find(x => x.id === this.control.value);
      if (colorValue) {
        this.setValue(colorValue);
      }
    }

    if (this.type === 'checkbox' && this.control.value) {
      this.inputElement.nativeElement.checked = true;
    }

    if (this.control && this.type === 'fake-number') {
      /**
       * On set la valeur initiale du control
       * pour ensuite pouvoir la comparer par la suite
       */
      if (this.control.value && typeof this.control.value === 'string') {
        this._initialFakeNumberValue = this._formatFakeNumber(this.control.value);
      } else if (this.control.value && typeof this.control.value === 'number') {
        this._initialFakeNumberValue = this._formatFakeNumber(this.control.value.toString());
      }

      /**
       * On set la nouvelle valeur du controler
       * afin qu'elle soit mise à jour dans l'input.
       *
       * Le paramètre emitViewToModelChange permet qu'il n'y ait pas
       * de valueChange émit. Cela permet que les valueChange des pages appelant ce fragment
       * ne soit pas déclenchés
       */
      this.control.setValue(this._initialFakeNumberValue, {
        emitViewToModelChange: false
      });
    }

    if (this.control && this.type === 'fake-int-number') {
      /**
       * On set la valeur initiale du control
       * pour ensuite pouvoir la comparer par la suite
       */
      if (this.control.value && typeof this.control.value === 'string') {
        this._initialFakeNumberValue = this.control.value;
      } else if (this.control.value && typeof this.control.value === 'number') {
        this._initialFakeNumberValue = this.control.value.toString();
      }

      /**
       * On set la nouvelle valeur du controler
       * afin qu'elle soit mise à jour dans l'input.
       *
       * Le paramètre emitViewToModelChange permet qu'il n'y ait pas
       * de valueChange émit. Cela permet que les valueChange des pages appelant ce fragment
       * ne soit pas déclenchés
       */
      this.control.setValue(this._initialFakeNumberValue, {
        emitViewToModelChange: false
      });
    }
    if (this.appAutoFocus) {
      this.inputElement.nativeElement.focus();
    }
  }

  public changedUnalignedCheckbox(control: any) {
    this.inputElement.nativeElement.checked = !this.inputElement.nativeElement.checked;
    control.setValue(this.inputElement.nativeElement.checked);
  }

  public async callSearchFunc(searchValue: string): Promise<any> {
    if (this.searchFunction && searchValue && searchValue !== '') {
      try {
        this.searchFunctionLoading = true;
        this.selectValue = [];
        this.selectValue = await this.searchFunction(searchValue);
      } finally {
        this.searchFunctionLoading = false;
      }
    }
  }

  public changed(control: AbstractControl): void {
    if (this.type === 'checkbox') {
      const isModelTrue = (ctrl: AbstractControl): boolean => {
        return (typeof ctrl.value === 'boolean' && ctrl.value) || ctrl.value === 'true';
      };
      setTimeout(() => {
        const modelValue = isModelTrue(control);
        control.setValue(modelValue, { emitViewToModelChange: false, emitEvent: false });
        this.inputElement.nativeElement.checked = modelValue;
        this.modelChanged.emit(modelValue);
      }, 0);
    } else {
      this.modelChanged.emit(control.value);
    }
  }

  public togglePasswordVisibility(): void {
    this.noeye = !this.noeye;
    if (this.inputElement.nativeElement.type === 'password') {
      this.inputElement.nativeElement.type = 'text';
    } else {
      this.inputElement.nativeElement.type = 'password';
    }
  }

  public deleteInputContent(): void {
    this.control.setValue('');
  }

  public setValue(value?: any): void {
    if (this.type === "colorSelect") {
      if (value) {
        this.control.setValue(value.id);
        if (value.libelle) {
          this.inputElement.nativeElement.style.setProperty('background-color', this.hexToRgba('#' + value.libelle));
          this.inputElement.nativeElement.style.setProperty('color', 'rgba(0,0,0,0)');
        }
      } else {
        this.control.setValue(undefined);
        this.inputElement.nativeElement.style.setProperty('background-color', 'rgba(0,0,0,0)');
        this.inputElement.nativeElement.style.setProperty('color', 'rgba(0,0,0,0)');
      }
    } else {
      if (value) {
        this.control.setValue(value);
      }
    }


  }

  onBlur(): void {
    this.blur.emit();
  }

  focusFunction() {
    if (this.selectOnFocus) {
      this.inputElement.nativeElement.select();
    }
  }

  public changeCheck(event): void {
    if (this.type === 'checkbox') {
      this.control.setValue(event.target.checked, { emitViewToModelChange: false, emitEvent: true });
    }
  }

  public hexToRgba(hex) {
    let color: any;
    if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
      color = hex.substring(1).split('');
      if (color.length == 3) {
        color = [color[0], color[0], color[1], color[1], color[2], color[2]];
      }
      color = '0x' + color.join('');
      return 'rgba(' + [(color >> 16) & 255, (color >> 8) & 255, color & 255].join(',') + ',0.5)';
    }
    throw new Error('Bad Hex');
  }

  private _formatFakeNumber(str: string): string {
    /**
     * Si la string contient des points ou des virgules
     * alors on la manipule pour la rendre conforme au format
     * attendu par la certification
     */
    if (str.includes('.') || str.includes(',')) {
      /**
       * On remplace les points par des virgules
       * pour être certain de n'avoir que des virgules pour la suite
       */
      str = str.replace(/\./g, ',');

      /**
       * Si plusieurs virgules sont présentes dans la string
       * alors on les retire toutes
       * puis on remet la première virgule à sa position initiale
       */
      const dotPos = str.indexOf(',');
      if (dotPos !== -1) {
        str = str.replace(/,/g, '');
        str = [str.slice(0, dotPos), ',', str.slice(dotPos)].join('');
      }

      /**
       * Si le premier caractère de la string est une virgule
       * alors on pad la string pour ajouter un 0 devant
       */
      if (str && str.charAt(0) === ',') {
        str = str.padStart(str.length + 1, '0');
      }
    }

    str = this._trimZeroStartFakeNumber(str);

    return str;
  }

  private _fakeNumberRemoveComa(str: string): string {
    if (str && str.charAt(str.length - 1) === ',') {
      str = str.slice(0, -1);
    }

    return str;
  }

  private _trimZeroStartFakeNumber(str: string): string {
    if (str && str.charAt(0) === '0' && str.charAt(1) && str.charAt(1) !== '' && str.charAt(1) !== ',') {
      str = str.substring(1);
      return this._trimZeroStartFakeNumber(str);
    } else {
      return str;
    }
  }

  private _trimZeroEndFakeNumber(str: string): string {
    if (str && str.includes(',') && str.charAt(str.length - 1) === '0') {
      str = str.slice(0, -1);
      str = this._fakeNumberRemoveComa(str);
      return this._trimZeroEndFakeNumber(str);
    } else {
      return str;
    }
  }

  public onfocusoutFakeNumber(abs: AbstractControl, evt: FocusEvent) {
    const currentValue = this._initialFakeNumberValue;

    // On ajoute un padend à la string
    this._initialFakeNumberValue = this._fakeNumberRemoveComa(abs.value);

    // On retire les 0 inutiles en fin de sting
    this._initialFakeNumberValue = this._trimZeroEndFakeNumber(this._initialFakeNumberValue);

    if (currentValue === this._initialFakeNumberValue) {
      return;
    }

    // On set la valeur au niveau de l'AbstractControl
    abs.setValue(this._initialFakeNumberValue, {
      emitViewToModelChange: false
    });

    // On appel changed() pour déclencher le modelChanged emmiter
    this.changed(abs);
  }

  public onKeyPressFakeIntNumber(abs: AbstractControl, evt: KeyboardEvent) {
    if (evt.code.toUpperCase() === 'TAB') {
      return;
    }

    /**
     * On récupère la position du curseur
     * Cela permettra de repositionner l'utilisateur
     * comme il faut en cas de saisie de caractère non valide
     */
    let start = (evt.target as any).selectionStart;

    /**
     * Si la string ne passe pas la regex
     * c'est qu'elle contient des caractères non autorisés
     *
     * Dans ce cas, on ne modifie pas la valeur
     * et on recalcule la position de curseur.
     *
     * Sinon, on utilise la nouvelle valeur comme valeur courante
     * mais on retire les 0 inutiles.
     */
    const reg = new RegExp(/^([0-9]*)?$/);
    if (reg.test(abs.value)) {
    this._initialFakeNumberValue = this._trimZeroStartFakeNumber(abs.value);
    } else {
      start = start - 1;
    }

    // On set la valeur au niveau de l'AbstractControl
    abs.setValue(this._initialFakeNumberValue);

    // On set la nouvelle position du curseur
    (evt.target as any).setSelectionRange(start, start);

    // On appel changed() pour déclencher le modelChanged emmiter
    this.changed(abs);
  }

  private _initialFakeNumberValue: string;
  public onKeyPressFakeNumber(abs: AbstractControl, evt: KeyboardEvent) {
    if (evt.code.toUpperCase() === 'TAB') {
      return;
    }

    /**
     * On récupère la position du curseur
     * Cela permettra de repositionner l'utilisateur
     * comme il faut en cas de saisie de caractère non valide
     */
    let start = (evt.target as any).selectionStart;

    /**
     * Si la string ne passe pas la regex
     * c'est qu'elle contient des caractères non autorisés
     *
     * Dans ce cas, on ne modifie pas la valeur
     * et on recalcule la position de curseur.
     *
     * Sinon, on format la valeur pour correspondre
     * aux règles attendues.
     */
    const reg = new RegExp(/^([0-9,.]*)?$/);
    if (reg.test(abs.value)) {
      this._initialFakeNumberValue = this._formatFakeNumber(abs.value);
    } else {
      start = start - 1;
    }

    // On set la valeur au niveau de l'AbstractControl
    abs.setValue(this._initialFakeNumberValue);

    // On set la nouvelle position du curseur
    (evt.target as any).setSelectionRange(start, start);

    // On appel changed() pour déclencher le modelChanged emmiter
    this.changed(abs);

  }

  private normalizeDateValue(value: any): any {
    if (!value) {
      return value;
    }

    // --- Moment ---
    if (moment.isMoment(value) && value.isValid()) {
      return value.clone().hour(12).minute(0).second(0).millisecond(0);
    }

    // --- Date native ---
    if (value instanceof Date && !isNaN(value.getTime())) {
      return new Date(
        value.getFullYear(),
        value.getMonth(),
        value.getDate(),
        12, 0, 0, 0
      );
    }

    return value;
  }

}
